import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";

export default function AuditNCR() {
  const { data, siteNames } = useHSE();
  const rows = data.ncrCapaRecords || data.ncrs || [];

  const handleSaveRecord = async (editingRecord) => {
    try {
      const res = await apiFetch("/ncrs", {
        method: "POST",
        body: {
          ...editingRecord,
          idempotencyKey: `ncr-${editingRecord.id}`,
        },
      });
      return res?.item ? { ...editingRecord, ...res.item } : editingRecord;
    } catch (err) {
      if (err.code === "SOD_VIOLATION" || err.status === 400) {
        throw new Error(err.message || "Segregation of Duties Violation: Assigned owner and independent verifier cannot be the same user.");
      }
      return editingRecord;
    }
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard label="Total Audit Findings / NCRs" value={rows.length} testid="ncr-total" />
      <StatCard label="Major NC" value={rows.filter((r) => r.findingType === "major_nc").length} tone="critical" />
      <StatCard label="Minor NC" value={rows.filter((r) => r.findingType === "minor_nc").length} tone="warning" />
      <StatCard label="Observations / OFI" value={rows.filter((r) => r.findingType === "observation" || r.findingType === "ofi").length} tone="info" />
      <StatCard label="Closed / Verified" value={rows.filter((r) => r.status === "closed").length} tone="safe" />
    </div>
  );

  return (
    <RegisterPage
      title="Audit Plans & NCR / CAPA Governance"
      subtitle="ISO 45001 Clause 9.2 & 10.2 — Internal/External Audit Plans, Non-Conformities & Independent Verification"
      storeKey="ncrCapaRecords"
      idPrefix="NCR"
      topPanels={Top}
      onSaveRecord={handleSaveRecord}
      columns={[
        { key: "id", label: "NCR ID" },
        { key: "findingType", label: "Finding Type", render: (r) => <span className={`uppercase text-[10px] font-bold px-2 py-0.5 rounded ${r.findingType === "major_nc" ? "bg-rose-100 text-rose-800" : r.findingType === "minor_nc" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{r.findingType || "minor_nc"}</span> },
        { key: "clauseReference", label: "ISO Clause" },
        { key: "title", label: "NCR Finding Title" },
        { key: "assignedToUserId", label: "Assigned Action Owner" },
        { key: "verifierUserId", label: "Independent Verifier" },
        { key: "targetDate", label: "Target Date" },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "NCR ID" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "sourceModule", label: "Source", type: "select", options: ["audit", "inspection", "incident", "legal_compliance", "management_review", "manual"] },
        { key: "findingType", label: "Finding Classification", type: "select", options: ["major_nc", "minor_nc", "observation", "ofi"] },
        { key: "clauseReference", label: "Standard / Clause Reference" },
        { key: "title", label: "Finding Title" },
        { key: "description", label: "Non-Conformity Description", type: "textarea", full: true },
        { key: "immediateCorrection", label: "Immediate Correction", type: "textarea", full: true },
        { key: "rootCauseAnalysis", label: "Root Cause Analysis (RCA)", type: "textarea", full: true },
        { key: "capaPlan", label: "CAPA Action Plan", type: "textarea", full: true },
        { key: "assignedToUserId", label: "Assigned Action Owner User ID" },
        { key: "verifierUserId", label: "Independent Verifier User ID (SoD Enforced)" },
        { key: "targetDate", label: "Target Completion Date", type: "date" },
        { key: "verificationNotes", label: "Verification & Closure Notes", type: "textarea", full: true },
        { key: "status", label: "Status", type: "select", options: ["open", "in_progress", "pending_verification", "closed"] },
      ]}
    />
  );
}
